import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { api } from '../lib/api';
import { SectionLabel, CTA, FloatingActions } from './Home';
import { PageHero } from './Services';
import useSeo from '../hooks/useSeo';

const Hub = ({ kind }) => {
  const isBlog = kind === 'blog';
  useSeo({
    title: isBlog ? 'Blogs' : 'News',
    description: isBlog
      ? 'Technical deep dives, BIM workflows and industry trend analysis from the Land Drafters engineering team.'
      : 'Project announcements, awards and updates from across Land Drafters\' global offices.',
    path: isBlog ? '/blogs' : '/news'
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [activeCat, setActiveCat] = useState('All');

  useEffect(() => {
    let alive = true;
    api.insights({ type: kind })
      .then(d => { if (alive) setItems(d.items || []); })
      .catch(e => { if (alive) setErr(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind]);

  const cats = ['All', ...Array.from(new Set(items.map(i => i.category)))];
  const filtered = activeCat === 'All' ? items : items.filter(i => i.category === activeCat);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <PageHero
        title={isBlog ? 'Engineering' : 'Company'}
        highlight={isBlog ? 'Blogs' : 'News'}
        typewriter={isBlog ? ['Industry Trends', 'Technical Deep-Dives', 'Best Practices'] : ['Latest Updates', 'Milestones', 'Partnerships']}
        subtitle={isBlog
          ? 'Technical articles and industry perspectives from our global engineering team.'
          : 'Announcements, milestones and product updates from across Land Drafters.'}
        breadcrumb={isBlog ? 'Knowledge Base' : 'Newsroom'}
      />

      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          {loading && (
            <div className="py-24 text-center text-gray-500 flex items-center justify-center gap-3">
              <Icons.Loader2 className="w-5 h-5 animate-spin text-green-600" /> Loading {isBlog ? 'blogs' : 'news'}…
            </div>
          )}
          {err && !loading && (
            <div className="py-12 text-center text-red-600 text-sm">Failed to load: {err}</div>
          )}
          {!loading && !err && items.length === 0 && (
            <div className="py-12 text-center text-gray-500">No {isBlog ? 'blogs' : 'news'} yet.</div>
          )}

          {!loading && !err && items.length > 0 && (
            <>
              {/* category chips */}
              <div className="flex flex-wrap items-center gap-2 mb-10">
                <span className="text-[10px] uppercase tracking-[0.2em] text-green-600 font-bold mr-1">Filter</span>
                {cats.map(c => (
                  <button
                    key={c}
                    onClick={() => setActiveCat(c)}
                    className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${activeCat === c ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-50 hover:text-green-700'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {/* featured */}
              {featured && (
                <Link to={`/insights/${featured.slug}`} className="group block relative overflow-hidden rounded-lg h-[440px] mb-10">
                  <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0e1116] via-[#0e1116]/60 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="inline-flex items-center gap-1.5 bg-gray-600 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded">
                      {isBlog ? <Icons.BookOpen className="w-3 h-3" /> : <Icons.Newspaper className="w-3 h-3" />}
                      {featured.category}
                    </div>
                    <h2 className="text-2xl md:text-4xl font-black mt-3 max-w-3xl group-hover:text-orange-400 transition-colors">{featured.title}</h2>
                    <p className="text-gray-300 mt-3 text-sm max-w-2xl line-clamp-2">{featured.excerpt}</p>
                    <div className="flex items-center gap-3 mt-4 text-xs text-gray-400">
                      <span>{featured.date}</span><span>·</span><span>{featured.read_time}</span>
                      {featured.views > 0 && (<><span>·</span><span className="flex items-center gap-1"><Icons.Eye className="w-3 h-3" /> {featured.views}</span></>)}
                    </div>
                  </div>
                </Link>
              )}

              {/* grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map(p => (
                  <Link key={p.slug} to={`/insights/${p.slug}`} className="group bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="h-52 overflow-hidden relative">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                      <div className="absolute top-3 left-3 bg-[#0e1116]/85 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded">{isBlog ? 'Blog' : 'News'}</div>
                    </div>
                    <div className="p-6">
                      <div className="inline-block bg-orange-50 text-orange-600 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded">{p.category}</div>
                      <h3 className="font-bold text-lg mt-3 text-[#0e1116] group-hover:text-green-600 transition-colors line-clamp-2">{p.title}</h3>
                      <p className="text-sm text-gray-500 mt-2 line-clamp-2">{p.excerpt}</p>
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                        <span>{p.date}</span>
                        <span className="flex items-center gap-1">
                          {p.views > 0 ? <><Icons.Eye className="w-3 h-3" /> {p.views}</> : p.read_time}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <CTA />
      <FloatingActions />
    </>
  );
};

export const BlogsPage = () => <Hub kind="blog" />;
export const NewsPage = () => <Hub kind="news" />;
